import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import * as turf from "@turf/turf"

import { KeyboardArrowLeftIcon, KeyboardArrowRightIcon } from "@l2r-front/l2r-icons"
import { useMapDispatchContext } from "@l2r-front/l2r-map"

import { useImageCollectionDispatchContext, useImageCollectionStateContext } from "../../contexts/ImageCollectionContext"
import { useImageCollection } from "../../hooks/queries/imageCollection/useImageCollection"

import { findAdjacentImage, findImageAfterSteps } from "./ImageNavigator.helpers"
import * as Styled from "./ImageNavigator.styled"
import { DisplayedImagePosition, ImageFeature } from "./ImageNavigator.types"

const WEST_BEARING = 270
const EAST_BEARING = 90
const NO_ARROW = 0
const LEFT_ARROW = 1
const RIGHT_ARROW = 2
const SHIFT_STEPS = 5

export function ImageNavigator() {
    const [lastArrowClicked, setLastArrowClicked] = useState<number>(NO_ARROW)
    const roadBearingRef = useRef<number | null>(null)

    const { imageCollection, selectedImageCollectionRoad, displayedImagePosition, triggeringFeatureId } = useImageCollectionStateContext()
    const { setImageFeatureClickedPosition } = useImageCollectionDispatchContext()
    const { getMapRef, setMapBoundingBoxToFit } = useMapDispatchContext()
    const mapRef = getMapRef()

    useEffect(() => {
        roadBearingRef.current = null
        setLastArrowClicked(NO_ARROW)
    }, [triggeringFeatureId])

    const {
        data: collectionLayer,
    } = useImageCollection(imageCollection, { linear_location_road: selectedImageCollectionRoad })

    const previousImage = useMemo(() => {
        if (!collectionLayer || !displayedImagePosition) {
            return null
        }

        const bearing = (lastArrowClicked === RIGHT_ARROW && roadBearingRef.current !== null) ? (roadBearingRef.current + 180) % 360 : roadBearingRef.current
        return findAdjacentImage(collectionLayer, displayedImagePosition, WEST_BEARING, bearing)
    }, [collectionLayer, displayedImagePosition, lastArrowClicked])

    const nextImage = useMemo(() => {
        if (!collectionLayer || !displayedImagePosition) {
            return null
        }

        const bearing = (lastArrowClicked === LEFT_ARROW && roadBearingRef.current !== null) ? (roadBearingRef.current + 180) % 360 : roadBearingRef.current
        return findAdjacentImage(collectionLayer, displayedImagePosition, EAST_BEARING, bearing)
    }, [collectionLayer, displayedImagePosition, lastArrowClicked])

    const handleArrowClick = useCallback((nextImageToDisplay: ImageFeature | null, arrow: number, steps = 1) => {
        if (!nextImageToDisplay || !displayedImagePosition) return

        const position = displayedImagePosition as DisplayedImagePosition
        const defaultBearing = arrow === LEFT_ARROW ? WEST_BEARING : EAST_BEARING
        const { image, bearing } = (steps > 1 && collectionLayer)
            ? findImageAfterSteps(collectionLayer, position, nextImageToDisplay, defaultBearing, steps)
            : { image: nextImageToDisplay, bearing: (turf.bearing(turf.point([position.lng, position.lat]), turf.point(nextImageToDisplay.geometry.coordinates)) + 360) % 360 }
        roadBearingRef.current = bearing

        setImageFeatureClickedPosition(image.geometry.coordinates)
        setLastArrowClicked(arrow)

        if (mapRef) {
            const map = mapRef.getMap()
            const [lng, lat] = image.geometry.coordinates
            setMapBoundingBoxToFit({
                minLng: lng,
                minLat: lat,
                maxLng: lng,
                maxLat: lat,
            }, map?.getZoom())
        }
    }, [collectionLayer, displayedImagePosition, mapRef, setImageFeatureClickedPosition, setMapBoundingBoxToFit])

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            const steps = event.shiftKey ? SHIFT_STEPS : 1
            if (event.key === "ArrowLeft") handleArrowClick(previousImage, LEFT_ARROW, steps)
            else if (event.key === "ArrowRight") handleArrowClick(nextImage, RIGHT_ARROW, steps)
        }

        window.addEventListener("keydown", handleKeyDown)
        return () => window.removeEventListener("keydown", handleKeyDown)
    }, [handleArrowClick, previousImage, nextImage])

    return <Styled.Container>
        <Styled.ArrowButton disabled={!previousImage} onClick={() => handleArrowClick(previousImage, LEFT_ARROW)}>
            <KeyboardArrowLeftIcon />
        </Styled.ArrowButton>
        <Styled.ArrowButton disabled={!nextImage} onClick={() => handleArrowClick(nextImage, RIGHT_ARROW)}>
            <KeyboardArrowRightIcon />
        </Styled.ArrowButton>
    </Styled.Container>
}
